/**
 * Non-component helpers for the AI APIs pages (A7Y-5559), kept out of the
 * `.tsx` files so those export only components (fast refresh). Provider label,
 * colour and console links come from `AI_API_PROVIDERS` in `@a7y/types`.
 */
import { useMemo, useState } from 'react';
import {
  AI_API_PROVIDERS,
  AI_API_PROVIDER_IDS,
  type AiApiProvider,
  type AiApiProviderInfo,
  type AiApiUsageSource,
} from '@a7y/types';

/** Overview card and Models tab order. */
export const PROVIDER_ORDER: readonly AiApiProvider[] = AI_API_PROVIDER_IDS;

export const EMPTY_PROVIDER_MESSAGE =
  'No usage recorded yet. The nightly snapshot fills this table at 05:35 CEST.';

export const CARD_CLASSES =
  'rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4';

export function providerInfo(provider: AiApiProvider): AiApiProviderInfo {
  return AI_API_PROVIDERS[provider];
}

const SOURCE_LABEL: Record<AiApiUsageSource, string> = {
  provider_cost: 'billed',
  provider_usage: 'rate table',
  call_log: 'call log',
};

export function sourceLabel(source: AiApiUsageSource): string {
  return SOURCE_LABEL[source];
}

/** `2026-10` → `Oct 2026`, in UTC (the month is a UTC calendar bucket). */
export function formatMonthLong(month: string): string {
  const [year, mon] = month.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, mon - 1, 1)));
}

/** `2026-10` → `Oct`, for chart ticks. */
export function formatMonthShort(month: string): string {
  const [year, mon] = month.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' }).format(
    new Date(Date.UTC(year, mon - 1, 1))
  );
}

/** Client-side sorting for one table; numbers sort numerically, everything else as text. */
export function useSortedRows<T, K extends string>(
  rows: T[],
  initial: K,
  read: (row: T, field: K) => string | number | null
) {
  const [sortField, setSortField] = useState<K>(initial);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = read(a, sortField);
      const bv = read(b, sortField);
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av ?? '').localeCompare(String(bv ?? ''));
      return sortOrder === 'asc' ? cmp : -cmp;
    });
    return copy;
    // `read` is a pure accessor declared per table; including it would re-sort on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sortField, sortOrder]);
  const onSort = (field: K) => {
    if (field === sortField) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else {
      setSortField(field);
      setSortOrder('desc');
    }
  };
  return { sorted, sortField, sortOrder, onSort };
}
